/* eslint-disable @typescript-eslint/no-unsafe-unary-minus */
/* eslint-disable @typescript-eslint/switch-exhaustiveness-check */
import { format } from 'date-fns';
import { saveAs } from '@upright/ui/common/generic/utilities/save-as';
import styled from 'styled-components';
import { GetRenderRequest } from '../../../../common/renderer/api-types';
import { defaultRenderFileType } from '../../../../common/renderer/renderer-settings';
import { RenderFileType, RenderOptions, RenderViewComponentType } from '../../../../common/renderer/types';
import { SpinnerButton } from '../../../common/generic/components/buttons/spinner-button';
import { useScreenshotMutation } from '../../../common/generic/hooks/use-take-screenshot';
import { useRenderViewContext } from '../context';

export const DownloadButton = () => {
  const { options } = useRenderViewContext();

  const text = `Download`;
  const loadingText = 'Generating...';

  const screenshotMutation = useScreenshotMutation();
  const generatingDocument = screenshotMutation.isPending;

  function takeScreenshot() {
    const data: GetRenderRequest = { options };
    screenshotMutation.mutate(
      { data },
      {
        // When server returns blob successfully, save as file
        onSuccess: response => {
          const filename = `${getFileStem(options)}-${getTimestamp()}.${getFileExtension(options.renderFileType)}`;
          saveAs(response, filename);
        },
      },
    );
  }

  return (
    <Root>
      <SpinnerButton onClick={takeScreenshot} disabled={generatingDocument} spinning={generatingDocument}>
        <b>{generatingDocument ? loadingText : text}</b>
      </SpinnerButton>
    </Root>
  );
};

const Root = styled.div``;

function getFileStem(options: Partial<RenderOptions>) {
  const defaultTitle = options.items?.[0]?.name || '';
  const defaultStem = `upright-image-export${defaultTitle.length ? `${-defaultTitle}` : ''}`;

  const components = options.componentParams;

  if (!components) throw Error(`Unable to download image - missing components to render`);

  const text = (() => {
    if (components.length > 1) return defaultTitle;

    const component = components[0];
    switch (component.componentType) {
      case RenderViewComponentType.LENS_NET_IMPACT: {
        const title = component.chartTitle || component.item.name;
        return `${getTitlePrefix(title)}upright-net-impact-profile`;
      }
      case RenderViewComponentType.IMPACT_CHART: {
        const title = component.chartTitle || defaultTitle;
        if (options.items && options.items.length > 1) {
          return `${getTitlePrefix(title)}upright-net-impact-comparison`;
        }
        return `${getTitlePrefix(title)}upright-net-impact-profile`;
      }
      case RenderViewComponentType.SDG_ALIGNMENTS: {
        const defaultSdgTitle = `${getTitlePrefix(component.item.name)}un-sdg-alignment`;
        const title = component.chartTitle || defaultSdgTitle;
        return `${title}-upright`;
      }
      default:
        return defaultStem;
    }
  })();
  // Replace all whitespace with "-"
  return text.toLowerCase().replace(/\s+/g, '-');
}

function getTitlePrefix(title: string | undefined) {
  return title ? `${title}-` : '';
}

function getTimestamp() {
  const date = new Date();
  return format(date, 'yyyyMMddHHmm');
}

function getFileExtension(type: RenderFileType | undefined) {
  switch (type) {
    case RenderFileType.PNG:
      return 'png';
    case RenderFileType.PDF:
      return 'pdf';
    default:
      return defaultRenderFileType;
  }
}
